import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';

import { BugsApiService } from '../bugs.service';
import { BUG_SEVERITY_OPTIONS, BUG_CATEGORY_OPTIONS, BUG_STATUS_OPTIONS, BugCommentDto, BugDto } from '../bugs.models';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-bugs-detail',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatProgressSpinnerModule, MatCardModule
  ],
  templateUrl: './bugs-detail.component.html',
  styleUrl: './bugs-detail.component.scss'
})
export class BugsDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly bugsApi = inject(BugsApiService);
  private readonly auth = inject(AuthService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly bug = signal<BugDto | null>(null);
  readonly comments = signal<BugCommentDto[]>([]);

  newComment = '';
  editStatus = 0;

  readonly severityOptions = BUG_SEVERITY_OPTIONS;
  readonly categoryOptions = BUG_CATEGORY_OPTIONS;
  readonly statusOptions = BUG_STATUS_OPTIONS;

  get bugId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.loadBug();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBug(): void {
    this.loading.set(true);
    this.bugsApi.getById(this.bugId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (b) => {
        this.bug.set(b);
        this.editStatus = b.status;
        this.comments.set(Array.isArray(b.comments) ? b.comments : []);
        this.loading.set(false);
        if (!b.comments?.length) this.loadComments();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load bug.');
        this.loading.set(false);
      }
    });
  }

  loadComments(): void {
    this.bugsApi.listComments(this.bugId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.comments.set(Array.isArray(items) ? items : []),
      error: () => this.comments.set([])
    });
  }

  goBack(): void {
    this.router.navigate(['/bugs']);
  }

  saveStatus(): void {
    const b = this.bug();
    if (!b) return;
    this.bugsApi.update(b.id, { status: this.editStatus }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadBug(),
      error: (err) => this.error.set(err?.message || 'Failed to update status.')
    });
  }

  addComment(): void {
    if (!this.newComment.trim()) return;
    this.saving.set(true);
    this.bugsApi.addComment(this.bugId, {
      authorId: this.auth.user()?.id,
      content: this.newComment.trim()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.newComment = '';
        this.saving.set(false);
        this.loadComments();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to add comment.');
        this.saving.set(false);
      }
    });
  }

  label(options: { value: number; label: string }[], v: number): string {
    return options.find(o => o.value === v)?.label ?? String(v);
  }
}
